import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { Noise } from '../core/noise.js';
import { CFG } from '../core/config.js';
import { clamp, clamp01, smoothstep, lerp } from '../core/mathx.js';
import { RIVER, ROADS, SITES, BRIDGE, Polyline, LANDFORM, HAS_RIVER, ARENA_ID, ARENA_NAME } from './layout.js';
import { tileFbm, tileWorley, detailTexture, colorTexture, dataTexture, vfbm, vnoise } from './texgen.js';
import { setShadowBox } from './mat.js';

/**
 * The battlefield heightfield.
 *
 * Authored, not noised: a broad valley floor, a rim of framing hills, a long
 * ridge in the north-east, a mesa in the south-west, the Brantas cut with its
 * sand banks, a road network flattened into the ground, and level pads under
 * every fort. Noise only ever *dresses* those shapes.
 *
 * Rendering is a concentric-ring LOD (1 m near the arena out to 152 m cells at
 * the horizon) shaded by a single splat material: a baked macro albedo/control
 * pair plus two tiling detail sets at three UV scales, so there is no visible
 * repeat and the ground still reads at eye level.
 */
const _px = new Uint8Array(4);
let _gl = null;

export function createTerrain(seed = 4242) {
  const noise = new Noise(seed);
  const SIZE = CFG.world.size, HALF = CFG.world.half;
  const RES = 512, STEP = SIZE / RES, W = RES + 1;

  /* ============================== shape ============================== */

  // -- broad valley: floor runs WNW→ESE through the arena
  /* The named geography comes from the ACTIVE ARENA (arenas.js). Every value
   * below used to be a literal here; they moved verbatim and the move was
   * proven a no-op by tools/arena-fingerprint.mjs. A feature an arena omits
   * contributes nothing, which is how a fortress interior has no valley. */
  const LF = LANDFORM;
  /** Laid ground — paving and raked sand — an arena declares; none on open country. */
  const GROUND = LF.ground || [];
  const _gw = { pave: 0, sand: 0, dirt: 0 };
  const VL = LF.valley;
  function valley(x, z) {
    if (!VL) return 0;
    const p = x * VL.nx + z * VL.nz;
    return -VL.depth * Math.exp(-(p * p) / (VL.width * VL.width)) +
      VL.lift * Math.exp(-(p * p) / (VL.liftWidth * VL.liftWidth));
  }

  // -- gentle rolling plain
  /* `plainAmp` scales it for an arena that was levelled by hand: a palace
   * compound stands on made ground, and a curtain wall cannot follow six
   * metres of roll. Omitted, it is 1 and this is the literal it always was. */
  const PLAIN_AMP = LF.plainAmp ?? 1;
  function plain(x, z) {
    let h = noise.fbm(x * 0.00415, z * 0.00415, 4, 2.15, 0.5) * 8.4;
    h += noise.fbm(x * 0.0168 + 31, z * 0.0168 - 17, 3, 2.2, 0.5) * 2.1;
    h += noise.fbm(x * 0.062, z * 0.062, 2) * 0.42;
    return h * PLAIN_AMP;
  }

  // -- rim hills that frame the fight, growing into distant mountains
  //
  // The skyline is a *composition* problem, not a terrain one: at eye level the
  // reference plate wants 20-40 % of the frame to be sky, which at a 52 deg
  // vertical FOV means the ridge line has to sit between +5 and +16 deg. The
  // first pass closed the bowl at ~20 deg (74 m foothills only 340 m out, then
  // 240 m peaks at 630 m) and no horizon frame contained any sky at all. So the
  // rim is pushed out and flattened: low forested foothills close in, the real
  // mass held back past 430 m where distance and aerial perspective can do the
  // work. Peak elevation from mid-field is now ~10-12 deg.
  /* Where the near foothills begin. An arena whose skyline is its own walls
   * pushes them out behind the curtain; the far ranges are untouched. */
  const RIM0 = LF.rimStart ?? 208;
  function rim(x, z) {
    const r = Math.hypot(x, z);
    if (r < RIM0) return 0;
    const t = smoothstep(clamp01((r - RIM0) / 180));
    const v = 0.45 + 0.85 * (noise.fbm(x * 0.0072 + 91, z * 0.0072 - 43, 4, 2.3, 0.55) * 0.5 + 0.5);
    const rg = 0.35 + 1.0 * (noise.ridged(x * 0.0128 - 17, z * 0.0128 + 61, 4, 2.4, 0.5) * 0.5 + 0.5);
    // The two distant masses used to start at a FIXED radius everywhere, and
    // both of their ramps finish by r = 1000. Past that the height is a large
    // constant (67v + 117rg, mean ~158 m) plus a small noise, so the maximum
    // elevation angle along every outward ray lands at the same radius with
    // nearly the same height: measured over 360 deg of azimuth from eye level,
    // the skyline sat at 11.8 deg with a standard deviation of 0.75 deg and a
    // total spread of 4 deg. That is a flat card, not a range.
    //
    // `rf` is a big low-frequency plan-form field (~900 m) that pushes each
    // mass in or out and scales it with the same sign, so some ranges stand
    // close and tall and others fall away past the far plane. It is gated to
    // zero inside 400 m, which is well outside the 260 m sampled grid, so the
    // playable heightfield is bit-identical (verified: max |dh| = 0 over the
    // whole 513x513 grid).
    const rf = noise.fbm(x * 0.0011 + 211, z * 0.0011 - 77, 3, 2.15, 0.55)
      * smoothstep(clamp01((r - 400) / 220));
    const push = 1 - 0.50 * rf;          // < 1 pulls the range towards the map
    const amp = 0.68 * (1 + 1.15 * rf);  // and makes it taller by the same sign
    const a2 = 430 * push, a3 = 700 * push;
    let farV = v, farRg = rg;
    if (r > 400) {
      farV = 0.45 + 0.85 * (
        noise.fbm(x * 0.0026 + 307, z * 0.0026 - 181, 3, 2.15, 0.52) * 0.5 + 0.5
      );
      farRg = 0.35 + 1.0 * (
        noise.ridged(x * 0.0031 - 127, z * 0.0031 + 233, 3, 2.2, 0.5) * 0.5 + 0.5
      );
    }
    // Keep the authored near foothills bit-identical, then replace detail
    // whose wavelength is below L3's Nyquist limit before it reaches L3.
    const farMix = smoothstep(clamp01((r - 400) / 180));
    const rimV = lerp(v, farV, farMix), rimRg = lerp(rg, farRg, farMix);
    let h = t * (17 * rimV + 19 * t * rimRg);
    if (r > a2) {
      // L3 resolves this non-playable range at 20 m. Reusing the 78 m
      // foothill signal (`rg`) for 100+ m of distant relief made adjacent L3
      // knots jump by as much as 106.8 m; the resulting giant diagonal faces
      // read as detached cards whenever the far plane cut through them. Keep
      // the near rim's sharper detail, but build the outer mass from signals
      // whose wavelengths the far mesh can actually sample.
      const t2 = smoothstep(clamp01((r - a2) / (330 * push)));
      h += t2 * (52 * farRg + 30 * farV) * amp;
      if (r > a3) {
        const t3 = smoothstep(clamp01((r - a3) / (300 * push)));
        h += t3 * (46 * farRg + 20 * farV) * amp;
      }
    }
    return h;
  }

  // -- long ridge, NE quadrant
  const RG = LF.ridge;
  function ridge(x, z) {
    if (!RG) return 0;
    const ax = RG.a[0], az = RG.a[1], dx = RG.b[0] - ax, dz = RG.b[1] - az;
    const L2 = dx * dx + dz * dz;
    let t = ((x - ax) * dx + (z - az) * dz) / L2;
    const tc = clamp01(t);
    const cx = ax + dx * tc, cz = az + dz * tc;
    const d = Math.hypot(x - cx, z - cz);
    if (d > RG.reach) return 0;
    const along = smoothstep(clamp01(t / 0.14)) * smoothstep(clamp01((1.06 - t) / 0.2));
    const prof = Math.exp(-(d * d) / (RG.sigma * RG.sigma));
    const rough = 0.6 + 0.55 * (noise.ridged(x * 0.0125 + 5, z * 0.0125 + 9, 3, 2.2, 0.5) * 0.5 + 0.5);
    return RG.height * prof * along * rough;
  }

  // -- signed distance to a rotated box (negative inside)
  function sdBox(px, pz, cx, cz, hx, hz, rot) {
    const c = Math.cos(-rot), s = Math.sin(-rot);
    const dx = px - cx, dz = pz - cz;
    const rx = dx * c - dz * s, rz = dx * s + dz * c;
    const qx = Math.abs(rx) - hx, qz = Math.abs(rz) - hz;
    const ox = Math.max(qx, 0), oz = Math.max(qz, 0);
    return Math.hypot(ox, oz) + Math.min(Math.max(qx, qz), 0);
  }

  // -- mesa in the SW: flat top, steep skirt
  const MS = LF.mesa;
  function plateau(x, z) {
    if (!MS) return 0;
    const d = sdBox(x, z, MS.x, MS.z, MS.hx, MS.hz, MS.rot);
    if (d > MS.skirt) return 0;
    const edge = 1 - smoothstep(clamp01((d + 4) / 30));
    // a natural mesa's top rolls; a built terrace (`flatTop`) is dressed level
    const top = MS.flatTop ? 1 : 0.86 + 0.28 * noise.fbm(x * 0.03 + 7, z * 0.03 - 3, 2);
    return MS.height * edge * top;
  }

  function knoll(x, z, cx, cz, r, h, wob = 0.25) {
    const d = Math.hypot(x - cx, z - cz);
    if (d > r) return 0;
    const w = 1 + wob * noise.fbm(x * 0.02 + cx, z * 0.02 + cz, 2);
    return h * Math.pow(1 - d / r, 1.7) * w;
  }

  const KN = LF.knolls || [];
  function hills(x, z) {
    let h = 0;
    for (let i = 0; i < KN.length; i++) h += knoll(x, z, KN[i][0], KN[i][1], KN[i][2], KN[i][3]);
    return h;
  }

  function hBaseRaw(x, z) {
    return plain(x, z) + valley(x, z) + rim(x, z) + ridge(x, z) + plateau(x, z) + hills(x, z);
  }

  // -- flatten the central arena so the fight always reads on fair ground
  const ARENA_Y = hBaseRaw(0, 0);
  /* An arena that is level already (`plainAmp`) sets this to 0: the pull is
   * toward the ORIGIN's height, and anything built up within 92 m of it — the
   * kraton's terrace edge at z = -52 — was dragged 1.5 m down by it. */
  const CENTRE_FLAT = LF.centreFlatten ?? 0.93;
  function hBase(x, z) {
    let h = hBaseRaw(x, z);
    const d = Math.hypot(x, z);
    const w = (1 - smoothstep(clamp01((d - 30) / 62))) * CENTRE_FLAT;
    if (w > 0.001) {
      const dress = noise.fbm(x * 0.028, z * 0.028, 2) * 0.55 + noise.fbm(x * 0.09, z * 0.09, 2) * 0.16;
      h = lerp(h, ARENA_Y + dress, w);
    }
    return h;
  }

  /* ---------------------- river + road arc-length profiles ---------------- */

  const _q = { d: 0, s: 0, cx: 0, cz: 0, tx: 0, tz: 0 };
  const _q2 = { d: 0, s: 0, cx: 0, cz: 0, tx: 0, tz: 0 };
  const _p = { x: 0, z: 0, tx: 0, tz: 0 };

  /** Sample a polyline's terrain height, smooth it, return a profile sampler. */
  function buildProfile(line, step, smoothM, opts = {}) {
    const n = Math.max(2, Math.ceil(line.len / step) + 1);
    const y = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      line.at(i * step, _p);
      y[i] = hBase(_p.x, _p.z);
    }
    // pull the profile toward base pads so roads meet forts flush
    if (opts.pads) {
      for (let i = 0; i < n; i++) {
        line.at(i * step, _p);
        for (const s of SITES) {
          if (s.pad <= 0) continue;
          const d = Math.hypot(_p.x - s.x, _p.z - s.z);
          const w = 1 - smoothstep(clamp01((d - s.pad * 0.5) / (s.pad * 1.1)));
          if (w > 0.001) y[i] = lerp(y[i], s.padY, w);
        }
      }
    }
    // wide box smooth → gentle grades
    const r = Math.max(1, Math.round(smoothM / step));
    const out = new Float32Array(n);
    for (let pass = 0; pass < 2; pass++) {
      const src = pass === 0 ? y : out;
      const dst = pass === 0 ? out : y;
      for (let i = 0; i < n; i++) {
        let sum = 0, cnt = 0;
        for (let k = -r; k <= r; k++) {
          const j = clamp(i + k, 0, n - 1);
          sum += src[j]; cnt++;
        }
        dst[i] = sum / cnt;
      }
    }
    if (opts.monotone) {
      // force a downhill watercourse
      for (let i = 1; i < n; i++) y[i] = Math.min(y[i], y[i - 1] - step * 0.006);
    }
    return { y, step, n, at(s) {
      const f = clamp(s / step, 0, n - 1.0001);
      const i = f | 0, t = f - i;
      return y[i] * (1 - t) + y[i + 1] * t;
    } };
  }

  for (const s of SITES) s.padY = hBase(s.x, s.z);

  const riverProf = buildProfile(RIVER, 4, 34, { monotone: true });
  for (const r of ROADS) r.prof = buildProfile(r.line, 3, 22, { pads: true });

  /* -------------------------- trampled trails ------------------------- */
  /**
   * Bare-earth tracks worn between the places troops actually walk. These are
   * albedo only — they never touch the heightfield — but they are what stops
   * the open valley from reading as one unbroken lawn: DW's fields are laced
   * with dirt desire-lines running off the metalled roads towards camps,
   * fords and hill posts.
   */
  function trailLine(ax, az, bx, bz, sd, wob) {
    const n = 10;
    const dx = bx - ax, dz = bz - az;
    const L = Math.hypot(dx, dz) || 1;
    const nx = -dz / L, nz = dx / L;
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const bulge = Math.sin(t * Math.PI);                 // pinned at both ends
      const o = noise.fbm(t * 2.6 + sd * 3.7, sd * 1.9, 3) * wob * bulge;
      pts.push([ax + dx * t + nx * o, az + dz * t + nz * o]);
    }
    return new Polyline(pts);
  }

  const TRAILS = [
    // fort approaches and camp links — the routes an army actually treads
    { line: trailLine(2, -150, -104, -96, 1.0, 34), hw: 2.4, feather: 4.6 },
    { line: trailLine(-150, -110, -60, -30, 2.0, 26), hw: 1.9, feather: 4.0 },
    { line: trailLine(168, -52, 96, 40, 3.0, 30), hw: 2.2, feather: 4.4 },
    { line: trailLine(124, 122, 30, 40, 4.0, 28), hw: 2.1, feather: 4.2 },
    { line: trailLine(14, 150, 110, 96, 5.0, 26), hw: 2.2, feather: 4.2 },
    { line: trailLine(-88, 27, -30, 84, 6.0, 30), hw: 1.9, feather: 4.0 },
    { line: trailLine(-40, -120, 90, -140, 7.0, 32), hw: 1.8, feather: 3.8 },
    { line: trailLine(-170, 60, -120, 150, 8.0, 24), hw: 1.7, feather: 3.8 },
    { line: trailLine(60, 30, 130, -70, 9.0, 30), hw: 1.8, feather: 3.8 },
    { line: trailLine(-20, 20, -110, -20, 10.0, 26), hw: 1.7, feather: 3.8 },
  ];

  /** 0..1 trampled-track weight (albedo only). */
  function trailWeightAt(x, z) {
    let best = 0;
    for (const t of TRAILS) {
      t.line.query(x, z, _q2);
      if (_q2.d > t.hw + t.feather) continue;
      const w = 1 - smoothstep(clamp01((_q2.d - t.hw) / t.feather));
      if (w > best) best = w;
    }
    return best;
  }

  /** River query → distance to centreline + local water-surface height. */
  function riverAt(x, z, out) {
    RIVER.query(x, z, out);
    out.y = riverProf.at(out.s);
    return out;
  }

  const CHANNEL = 7.5, BANK = 30.0, DEPTH = 4.6;

  /**
   * The water LINE at arc-length s, and the half-width of the strip there.
   *
   * Factored out because the first version of `waterAt()` did not use it and
   * was wrong by 4.05 m as a result: it took `riverProf.at(s)` — the BANK-TOP
   * reference the channel is cut down from — as the water surface, where the
   * drawn mesh sits at `riverProf.at(s) - DEPTH + 0.55`. It then reported
   * eight metres of water on a dry bamboo terrace, and the contract check I
   * wrote passed on it, because it only asserted that deep water slows you
   * down and never that the water was where the player could see it.
   *
   * The mesh builder and the query now read the same two lines.
   */
  function waterLineAt(s) { return riverProf.at(s) - DEPTH + 0.55 + 0.25 * Math.sin(s * 0.06); }
  function waterHalfWidth(s) { return 3.4 + 2.6 * (0.5 + 0.5 * Math.sin(s * 0.031 + 1.7)); }

  function hRiver(x, z) {
    let h = hBase(x, z);
    riverAt(x, z, _q);
    const d = _q.d;
    if (d > BANK + 16) return h;
    const bed = _q.y - DEPTH;
    // smootherstep bank so the cut reads as an eroded valley, not a ditch
    let t = clamp01((d - CHANNEL) / (BANK - CHANNEL));
    t = t * t * t * (t * (t * 6 - 15) + 10);
    const inner = 1 - t;
    if (inner > 0.001) h = lerp(h, Math.min(h, bed + t * DEPTH * 1.02), inner);
    const lip = Math.exp(-Math.pow((d - BANK - 5) / 14, 2));
    h += 1.1 * lip;
    return h;
  }

  /** Road weight at a point: 1 on the carriageway, feathering to 0. */
  function roadWeightAt(x, z, out) {
    let best = 0, bestY = 0, bestD = 1e9, bestHw = 3;
    for (const r of ROADS) {
      r.line.query(x, z, _q2);
      const w = 1 - smoothstep(clamp01((_q2.d - r.hw) / r.feather));
      if (w > best) { best = w; bestY = r.prof.at(_q2.s); bestD = _q2.d; bestHw = r.hw; }
      else if (best === 0 && _q2.d < bestD) { bestD = _q2.d; bestHw = r.hw; }
    }
    out.w = best; out.y = bestY; out.d = bestD; out.hw = bestHw;
    return out;
  }

  const _rw = { w: 0, y: 0, d: 0, hw: 3 };

  /** The final surface. Used for every rendered vertex AND for the grid. */
  function shapeFull(x, z) {
    let h = hRiver(x, z);
    // roads — suppressed inside the river channel so the bridge can span it
    roadWeightAt(x, z, _rw);
    if (_rw.w > 0.002) {
      RIVER.query(x, z, _q);
      const gap = smoothstep(clamp01((_q.d - CHANNEL - 1) / 11));
      const w = _rw.w * gap;
      if (w > 0.002) h = lerp(h, _rw.y, w);
    }
    // base pads
    for (const s of SITES) {
      if (s.pad <= 0) continue;
      const d = Math.hypot(x - s.x, z - s.z);
      if (d > s.pad + s.padFeather) continue;
      const w = 1 - smoothstep(clamp01((d - s.pad) / s.padFeather));
      h = lerp(h, s.padY, w);
    }
    return h;
  }

  /* ============================ sampled grid ========================== */

  const heights = new Float32Array(W * W);
  const matId = new Uint8Array(W * W);
  const roadG = new Uint8Array(W * W);
  const roadDG = new Uint8Array(W * W);   // metres to the nearest carriageway
  const trailG = new Uint8Array(W * W);   // trampled-track weight
  const rivG = new Uint8Array(W * W);
  let minH = 1e9, maxH = -1e9;

  for (let j = 0; j < W; j++) {
    const z = -HALF + j * STEP;
    for (let i = 0; i < W; i++) {
      const x = -HALF + i * STEP;
      const h = shapeFull(x, z);
      const k = j * W + i;
      heights[k] = h;
      if (h < minH) minH = h; if (h > maxH) maxH = h;
      roadWeightAt(x, z, _rw);
      roadG[k] = Math.round(clamp01(_rw.w) * 255);
      roadDG[k] = Math.min(255, Math.round(_rw.d));
      trailG[k] = Math.round(trailWeightAt(x, z) * 255);
      RIVER.query(x, z, _q);
      rivG[k] = Math.min(255, Math.round(_q.d));
    }
  }

  function heightAt(x, z) {
    const fx = clamp((x + HALF) / STEP, 0, RES - 1e-4);
    const fz = clamp((z + HALF) / STEP, 0, RES - 1e-4);
    const i = fx | 0, j = fz | 0, tx = fx - i, tz = fz - j;
    const a = heights[j * W + i], b = heights[j * W + i + 1];
    const c = heights[(j + 1) * W + i], d = heights[(j + 1) * W + i + 1];
    return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
  }

  const _n = new THREE.Vector3();
  function normalAt(x, z, out = _n) {
    const e = 1.1;
    const hl = heightAt(x - e, z), hr = heightAt(x + e, z);
    const hd = heightAt(x, z - e), hu = heightAt(x, z + e);
    return out.set(hl - hr, 2 * e, hd - hu).normalize();
  }
  function slopeAt(x, z) { return 1 - normalAt(x, z, _n).y; }

  /* ------------------------- walkable platforms ------------------------- */
  /**
   * Raised surfaces that something can stand ON, above `heightAt`.
   *
   * The bridge deck is geometry only: `heightAt()` returns the gorge floor
   * underneath it, and every body in the game is ground-clamped to that, so a
   * squad ordered across the Brantas walks down into the channel, through the
   * water and up the far bank — 5.2 m below the deck, passing under its own
   * arches. Carving the deck into the heightfield would fix that one bridge
   * and leave the class open (a rampart walk, a fort stair, a jetty all have
   * it today). So: a registry, and one call that returns the topmost walkable
   * surface. Anything that ground-clamps should use `walkHeightAt` instead of
   * `heightAt`; `heightAt` keeps meaning "the ground", which is what drainage,
   * placement, splat and the heightfield itself still want.
   */
  const walkways = [];

  /**
   * @param {object} p
   *  x, z, rot   centre and heading of the platform (bases' own site frame)
   *  hx, hz      half extents of the CLEAR walking surface, platform-local
   *  y           world height of the surface
   *  ramp        metres of blend to the ground off each end (+-x)
   *  feather     metres of blend off each side (+-z)
   */
  function addWalkway(p) {
    const ramp = p.ramp ?? 4.0, feather = p.feather ?? 1.0;
    const reach = Math.hypot(p.hx + ramp, p.hz + feather);
    walkways.push({
      x: p.x, z: p.z, ca: Math.cos(p.rot || 0), sa: Math.sin(p.rot || 0),
      hx: p.hx, hz: p.hz, y: p.y, ramp, feather, r2: reach * reach, tag: p.tag || '',
    });
    return walkways[walkways.length - 1];
  }

  /**
   * Topmost walkable surface at (x, z). Identical to `heightAt` everywhere no
   * platform covers, continuous across every platform edge (so a body clamped
   * to it walks up the approach instead of popping), and never lower than the
   * ground. Allocation free; one squared-radius reject per platform.
   */
  function walkHeightAt(x, z) {
    let h = heightAt(x, z);
    for (let i = 0; i < walkways.length; i++) {
      const p = walkways[i];
      const dx = x - p.x, dz = z - p.z;
      if (dx * dx + dz * dz > p.r2) continue;
      const lx = dx * p.ca - dz * p.sa, lz = dx * p.sa + dz * p.ca;
      const ax = Math.abs(lx) - p.hx, az = Math.abs(lz) - p.hz;
      if (ax > p.ramp || az > p.feather) continue;
      let t = 1;
      if (ax > 0) t *= 1 - smoothstep(clamp01(ax / p.ramp));
      if (az > 0) t *= 1 - smoothstep(clamp01(az / p.feather));
      const y = h + (p.y - h) * t;
      if (y > h) h = y;
    }
    return h;
  }

  /** True if (x, z) is standing on a registered platform rather than the ground. */
  function onWalkway(x, z) { return walkHeightAt(x, z) > heightAt(x, z) + 0.05; }

  function gridIdx(x, z) {
    const i = clamp(Math.round((x + HALF) / STEP), 0, RES);
    const j = clamp(Math.round((z + HALF) / STEP), 0, RES);
    return j * W + i;
  }

  // classify: 0 grass 1 dirt 2 road 3 stone 4 sand  (+ cache the slope field)
  const slopeG = new Float32Array(W * W);
  for (let j = 0; j < W; j++) {
    const z = -HALF + j * STEP;
    for (let i = 0; i < W; i++) {
      const x = -HALF + i * STEP, k = j * W + i;
      const road = roadG[k] / 255, riv = rivG[k];
      const hl = heights[j * W + Math.max(0, i - 1)], hr = heights[j * W + Math.min(RES, i + 1)];
      const hd = heights[Math.max(0, j - 1) * W + i], hu = heights[Math.min(RES, j + 1) * W + i];
      const sl = Math.hypot(hl - hr, hd - hu) / (2 * STEP);
      slopeG[k] = sl;
      let m = 0;
      const dirtN = noise.fbm(x * 0.011 + 51, z * 0.011 - 29, 3);
      if (dirtN > 0.30) m = 1;
      if (sl > 0.60 || heights[k] > 46) m = 3;
      if (riv < 16) m = 4;
      if (road > 0.5) m = 2;
      // trampled ground inside base footprints
      for (const s of SITES) {
        if (s.pad <= 0) continue;
        if (Math.hypot(x - s.x, z - s.z) < s.pad * 0.85 && m !== 2) m = 1;
      }
      // laid ground: stone where it is paved, sand in a raked square
      if (GROUND.length) {
        groundAt(x, z, _gw);
        if (_gw.pave > 0.5) m = 3;
        else if (_gw.sand > 0.5 && m !== 2) m = 4;       // causeways stay road
        else if (_gw.dirt > 0.5 && m === 0) m = 1;       // a camp's trodden floor
      }
      matId[k] = m;
    }
  }

  function materialAt(x, z) { return matId[gridIdx(x, z)]; }

  /** Weights of the arena's laid ground at (x,z), written into `_gw`. */
  function groundAt(x, z, out) {
    out.pave = 0; out.sand = 0; out.dirt = 0;
    for (let k = 0; k < GROUND.length; k++) {
      const g = GROUND[k];
      // inside distance to the rectangle, feathered at its edge
      const d = Math.min(x - g.x0, g.x1 - x, z - g.z0, g.z1 - z);
      if (d <= -g.feather) continue;
      const wgt = smoothstep(clamp01((d + g.feather) / (g.feather * 2)));
      if (g.kind === 'pave') out.pave = Math.max(out.pave, wgt);
      else if (g.kind === 'sand') { out.sand = Math.max(out.sand, wgt); out.pave *= 1 - wgt; }
      else if (g.kind === 'dirt') out.dirt = Math.max(out.dirt, wgt);
    }
    return out;
  }

  /* ======================= procedural texture set ===================== */

  const DN = 256;
  // grass / soil micro-surface: fibrous streaks over a soft clump field
  const gA = tileFbm(DN, 5, 1201, 4, 0.55);
  const gB = tileFbm(DN, 22, 1307, 3, 0.5);
  const gC = tileFbm(DN, 61, 1409, 2, 0.5);
  const gE = tileWorley(DN, 26, 1511, 1.0);
  const hA = new Float32Array(DN * DN), lA = new Float32Array(DN * DN), oA = new Float32Array(DN * DN);
  for (let j = 0; j < DN; j++) for (let i = 0; i < DN; i++) {
    const k = j * DN + i;
    // clumps of turf (worley) broken by fine fibre noise — no directional streaks
    const clump = Math.pow(1 - gE[k], 1.5);
    const h = gB[k] * 0.34 + gC[k] * 0.30 + clump * 0.36;
    hA[k] = h;
    lA[k] = clamp01(0.22 + h * 0.74 + (gA[k] - 0.5) * 0.42 + (gC[k] - 0.5) * 0.30);
    oA[k] = clamp01(0.40 + (gB[k] * 0.5 + clump * 0.5) * 0.85);
  }
  const detA = detailTexture(DN, hA, lA, oA, 4.2);

  // rock / gravel: packed pebbles with a grit overlay
  const wA = tileWorley(DN, 13, 2203, 0.95);
  const wB = tileWorley(DN, 34, 2311, 1.0);
  const gD = tileFbm(DN, 44, 2417, 3, 0.5);
  const hB = new Float32Array(DN * DN), lB = new Float32Array(DN * DN), oB = new Float32Array(DN * DN);
  for (let j = 0; j < DN; j++) for (let i = 0; i < DN; i++) {
    const k = j * DN + i;
    const peb = Math.pow(1 - wA[k], 1.6) * 0.62 + Math.pow(1 - wB[k], 2.0) * 0.3;
    const h = peb + gD[k] * 0.22;
    hB[k] = h;
    lB[k] = clamp01(0.26 + h * 0.9 + (gD[k] - 0.5) * 0.24);
    oB[k] = clamp01(0.34 + (1 - Math.pow(1 - wA[k], 2.2)) * 0.8);
  }
  const detB = detailTexture(DN, hB, lB, oB, 9.0);

  /* ---------------------- baked macro albedo + control ------------------- */

  const MRES = 1024;
  const macroData = new Uint8Array(MRES * MRES * 4);

  // coarse variation grids (bilinear-sampled per texel — far cheaper than fbm)
  const VN = 256;
  const vLarge = new Float32Array(VN * VN), vMed = new Float32Array(VN * VN), vDry = new Float32Array(VN * VN);
  const vWarpA = new Float32Array(VN * VN), vWarpB = new Float32Array(VN * VN);
  for (let j = 0; j < VN; j++) for (let i = 0; i < VN; i++) {
    const x = (i / VN) * SIZE - HALF, z = (j / VN) * SIZE - HALF;
    const k = j * VN + i;
    vLarge[k] = vfbm(x * 0.0055, z * 0.0055, 771, 3);
    vMed[k] = vfbm(x * 0.021, z * 0.021, 913, 3);
    vDry[k] = vfbm(x * 0.0125 + 40, z * 0.0125 - 12, 1553, 3);
    vWarpA[k] = vfbm(x * 0.0092 + 7, z * 0.0092 + 3, 2411, 2);
    vWarpB[k] = vfbm(x * 0.0092 - 4, z * 0.0092 - 9, 2707, 2);
  }
  // finer grids: patch blobs (~25 m), stone scree clusters (~11 m) and the
  // 6 m clumping that ragged-edges every boundary above. 1 m per cell.
  const VF = 512;
  const vPatch = new Float32Array(VF * VF), vScree = new Float32Array(VF * VF), vClump = new Float32Array(VF * VF);
  for (let j = 0; j < VF; j++) for (let i = 0; i < VF; i++) {
    const x = (i / VF) * SIZE - HALF, z = (j / VF) * SIZE - HALF;
    const k = j * VF + i;
    vPatch[k] = vfbm(x * 0.040 + 13, z * 0.040 - 21, 3121, 3);
    vScree[k] = vfbm(x * 0.088 - 33, z * 0.088 + 17, 3607, 2);
    vClump[k] = vfbm(x * 0.155 + 5, z * 0.155 + 29, 4001, 2);
  }

  /**
   * fbm of value noise piles up hard around 0.5 (sd ~0.08), so a threshold
   * written as "nD > 0.68" only ever opens on a couple of per cent of the map
   * — which is exactly why the first pass baked one unbroken grass sheet.
   * Standardise every control field to mean 0.5 / sd ~0.3 so the thresholds
   * below mean what they say and coverage is predictable.
   */
  function standardise(arr, spread = 3.1) {
    let s = 0, s2 = 0;
    for (let k = 0; k < arr.length; k++) { s += arr[k]; s2 += arr[k] * arr[k]; }
    const m = s / arr.length;
    const sd = Math.sqrt(Math.max(1e-9, s2 / arr.length - m * m));
    const inv = 1 / (spread * sd);
    for (let k = 0; k < arr.length; k++) arr[k] = clamp01(0.5 + (arr[k] - m) * inv);
    return arr;
  }
  standardise(vLarge); standardise(vMed); standardise(vDry);
  standardise(vPatch); standardise(vScree); standardise(vClump);
  function sampleG(arr, n, u, v) {
    const fu = clamp(u * (n - 1), 0, n - 1.0001), fv = clamp(v * (n - 1), 0, n - 1.0001);
    const i = fu | 0, j = fv | 0, tu = fu - i, tv = fv - j;
    const a = arr[j * n + i], b = arr[j * n + i + 1], c = arr[(j + 1) * n + i], d = arr[(j + 1) * n + i + 1];
    return (a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv;
  }
  /** Same, addressed in world metres (used for domain-warped lookups). */
  function sampleW(arr, n, x, z) {
    return sampleG(arr, n, (x + HALF) / SIZE, (z + HALF) / SIZE);
  }
  function sampleH(u, v) {
    const fu = clamp(u * RES, 0, RES - 1e-4), fv = clamp(v * RES, 0, RES - 1e-4);
    const i = fu | 0, j = fv | 0, tu = fu - i, tv = fv - j;
    const a = heights[j * W + i], b = heights[j * W + i + 1], c = heights[(j + 1) * W + i], d = heights[(j + 1) * W + i + 1];
    return (a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv;
  }
  function sampleF(arr, u, v) {
    const fu = clamp(u * RES, 0, RES - 1e-4), fv = clamp(v * RES, 0, RES - 1e-4);
    const i = fu | 0, j = fv | 0, tu = fu - i, tv = fv - j;
    const a = arr[j * W + i], b = arr[j * W + i + 1], c = arr[(j + 1) * W + i], d = arr[(j + 1) * W + i + 1];
    return (a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv;
  }
  function sampleU8(arr, u, v) {
    const fu = clamp(u * RES, 0, RES - 1e-4), fv = clamp(v * RES, 0, RES - 1e-4);
    const i = fu | 0, j = fv | 0, tu = fu - i, tv = fv - j;
    const a = arr[j * W + i], b = arr[j * W + i + 1], c = arr[(j + 1) * W + i], d = arr[(j + 1) * W + i + 1];
    return ((a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv) / 255;
  }

  // palette (sRGB bytes)
  const C = {
    grassLush: [48, 90, 30], grassOlive: [78, 102, 44], grassDry: [140, 128, 70],
    grassStraw: [142, 130, 84], grassPale: [104, 118, 62], grassBurnt: [116, 100, 56],
    dirt: [122, 97, 64], dirtRed: [130, 90, 58], dirtDark: [80, 61, 41],
    packed: [152, 131, 96], dust: [163, 145, 112],
    road: [126, 110, 84], rut: [78, 65, 48], gravel: [140, 130, 113],
    rock: [116, 109, 98], rockDark: [70, 66, 61], rockPale: [148, 141, 128],
    // laid ground (arena `ground`): andesite paving and a raked sand court
    pave: [132, 126, 116], paveDark: [98, 93, 86], pavePale: [156, 150, 138], paveJoint: [58, 54, 50],
    courtSand: [158, 140, 106], courtSandDark: [128, 110, 80],
    sand: [138, 122, 92], sandWet: [96, 86, 66], silt: [116, 108, 88],
    wet: [48, 45, 39], mud: [66, 55, 42],
    wheat: [166, 146, 74], millet: [122, 128, 56], furrow: [96, 78, 48],
  };
  // hand-placed cultivated plots (wheat / millet strips) — pure map dressing
  const FIELDS = LF.fields || [];
  /**
   * Cultivated plots. The boundary query runs in a *warped* domain and the
   * feather is wide, so the plots read as hand-cleared ground following the
   * lie of the land instead of the hard rectangular plaid the first pass baked
   * (clearly visible as strips at distance).
   */
  const _fld = { w: 0, hue: 0, fur: 0 };
  function fieldAt(x, z) {
    _fld.w = 0; _fld.hue = 0; _fld.fur = 0;
    let near = false;
    for (const f of FIELDS) {
      if (Math.abs(x - f.x) < f.hx + f.hz + 26 && Math.abs(z - f.z) < f.hx + f.hz + 26) { near = true; break; }
    }
    if (!near) return _fld;
    const wx = x + noise.fbm(x * 0.028 + 3, z * 0.028 - 8, 3) * 9.5 + noise.fbm(x * 0.11, z * 0.11, 2) * 2.2;
    const wz = z + noise.fbm(x * 0.028 - 5, z * 0.028 + 2, 3) * 9.5 + noise.fbm(x * 0.11 + 9, z * 0.11 - 4, 2) * 2.2;
    let best = 0, hue = 0, fur = 0;
    for (const f of FIELDS) {
      const d = sdBox(wx, wz, f.x, f.z, f.hx, f.hz, f.rot);
      if (d > 9) continue;
      const w = 1 - smoothstep(clamp01((d + 4.0) / 12));
      if (w > best) {
        best = w; hue = f.hue;
        const c = Math.cos(f.rot), sn = Math.sin(f.rot);
        const rx = (wx - f.x) * c - (wz - f.z) * sn;
        fur = 0.5 + 0.5 * Math.sin(rx * 1.15);
      }
    }
    _fld.w = best; _fld.hue = hue; _fld.fur = fur;
    return _fld;
  }

  const _c = [0, 0, 0];
  function mixC(a, b, t, out) { out[0] = a[0] + (b[0] - a[0]) * t; out[1] = a[1] + (b[1] - a[1]) * t; out[2] = a[2] + (b[2] - a[2]) * t; return out; }

  const invM = 1 / MRES;
  for (let j = 0; j < MRES; j++) {
    const v = (j + 0.5) * invM;
    const z = v * SIZE - HALF;
    for (let i = 0; i < MRES; i++) {
      const u = (i + 0.5) * invM;
      const x = u * SIZE - HALF;

      const h = sampleH(u, v);
      const sl = sampleF(slopeG, u, v);

      const road = sampleU8(roadG, u, v);
      const roadD = sampleU8(roadDG, u, v) * 255;
      const trail = sampleU8(trailG, u, v);
      const rivD = sampleU8(rivG, u, v) * 255;

      const nL = sampleG(vLarge, VN, u, v);
      const nM = sampleG(vMed, VN, u, v);
      const nD = sampleG(vDry, VN, u, v);
      // domain warp: every boundary below is queried through this offset, so
      // nothing keeps the smooth elliptical outline of the noise that made it.
      const wox = (sampleG(vWarpA, VN, u, v) - 0.5) * 44;
      const woz = (sampleG(vWarpB, VN, u, v) - 0.5) * 44;
      const nP = sampleW(vPatch, VF, x + wox, z + woz);
      const nS = sampleW(vScree, VF, x + wox * 0.45, z + woz * 0.45);
      const nC = sampleW(vClump, VF, x + wox * 0.18, z + woz * 0.18);
      const grain = vnoise(x * 0.55, z * 0.55, 4409);
      const fine = vnoise(x * 1.85 + 11, z * 1.85 - 7, 5501);

      // local concavity — hollows hold water and get trodden into mud first
      const o = invM * 7;
      const wide = (sampleH(u + o, v) + sampleH(u - o, v) + sampleH(u, v + o) + sampleH(u, v - o)) * 0.25;
      const conc = clamp01((wide - h) * 4.5);

      /* ---- layer weights -------------------------------------------------
       * The first pass left the whole valley floor as one grassLush sheet
       * (dirt only opened above nD > 0.68 and capped at 0.72), which measured
       * an albedo sd of ~(9,14,4) — a flat green lawn. DW never does that: the
       * open field is broken by bare earth, dry straw, trodden tracks, stone
       * scree and standing water, all with ragged multi-scale boundaries. Every
       * weight below is driven by at least two frequencies through the warp. */
      let wRoad = road;
      let wSand = (1 - smoothstep(clamp01((rivD - 8) / 13))) * (0.62 + 0.5 * nM);
      // eroded gravel skirt up the banks so they don't read as flat dark grass
      wSand = Math.max(wSand, (1 - smoothstep(clamp01((rivD - 13) / 17))) * smoothstep(clamp01((sl - 0.10) / 0.18)) * 0.85);
      // ...but a bank is a braid of bars and vegetated islands, not a sheet:
      // hold the sand back wherever the clump field says something is growing.
      wSand *= 0.42 + 0.72 * smoothstep(clamp01((nC + nP - 0.72) / 0.55));

      // stone: slope/altitude scree, plus loose boulder fields out on the flat
      const scree = smoothstep(clamp01((nS - 0.66) / 0.12)) * smoothstep(clamp01((nP - 0.45) / 0.24)) * (0.35 + 0.65 * nC);
      let wRock = clamp01(smoothstep(clamp01((sl - 0.52) / 0.26)) * 1.15 + clamp01((h - 52) / 60) * 0.35);
      wRock = clamp01(Math.max(wRock, scree * 0.85));

      // bare earth: warped 25 m blobs broken by the 6 m clump field
      const bareN = nP * 0.55 + nC * 0.25 + nM * 0.20;
      let wDirt = smoothstep(clamp01((bareN - 0.625) / 0.135)) * (0.55 + 0.45 * nC);
      // trodden desire-lines and the worn apron either side of every road. The
      // track weight is broken along its length by the patch field so a trail
      // fades in and out of the turf instead of running as a painted stripe.
      const track = trail * clamp01(0.34 + 1.05 * nP) * (0.62 + 0.38 * nC);
      wDirt = Math.max(wDirt, track);
      const apron = (1 - smoothstep(clamp01((roadD - 2.5) / 7))) * (0.25 + 0.60 * nC) * (1 - road);
      wDirt = Math.max(wDirt, apron * 0.66);
      let padD = 1;
      for (const s of SITES) {
        if (s.pad <= 0) continue;
        // warped radius so the trampled apron round a fort is not a clean ring
        const d = Math.hypot(x + wox * 0.42 - s.x, z + woz * 0.42 - s.z);
        if (d < s.pad * 1.35) {
          const t = smoothstep(clamp01((d - s.pad * 0.45) / (s.pad * 0.75)));
          wDirt = Math.max(wDirt, (1 - t) * (0.62 + 0.32 * nC));
          padD = Math.min(padD, t);
        }
      }
      wSand = clamp01(wSand);
      wDirt = clamp01(wDirt * (1 - wRock * 0.6));
      const used = clamp01(wRoad + wSand * (1 - wRoad) + wRock * (1 - wRoad) + wDirt * (1 - wRoad));
      const wGrass = clamp01(1 - used);

      /* ---- grass: a four-stop ramp, not two ----------------------------- */
      const dry = clamp01(0.25 + (nD - 0.5) * 0.80 + (nL - 0.5) * 0.42 + (nP - 0.5) * 0.30
        + clamp01((h - ARENA_Y - 5) / 44) * 0.22 + sl * 0.50 + (grain - 0.5) * 0.16 + (fine - 0.5) * 0.09);
      mixC(C.grassLush, C.grassOlive, smoothstep(clamp01(dry / 0.44)), _c);
      mixC(_c, C.grassDry, smoothstep(clamp01((dry - 0.42) / 0.30)), _c);
      mixC(_c, C.grassStraw, smoothstep(clamp01((dry - 0.74) / 0.26)) * 0.72, _c);
      // patchy pale/burnt mottle at two more scales so no area is one tone
      mixC(_c, C.grassPale, clamp01(nM * 0.30 + nC * 0.26), _c);
      mixC(_c, C.grassBurnt, smoothstep(clamp01((nC - 0.60) / 0.24)) * 0.34 * (0.4 + 0.6 * dry), _c);
      let r = _c[0], g = _c[1], b = _c[2];
      const gv = Math.min(1.22, 0.84 + 0.32 * nM + 0.14 * grain + 0.11 * fine);
      r *= gv; g *= gv; b *= gv;
      const fld = fieldAt(x, z);
      if (fld.w > 0.002) {
        mixC(fld.hue ? C.millet : C.wheat, C.furrow, clamp01(fld.fur * 0.48 + grain * 0.30 + nC * 0.18), _c);
        const k = fld.w * 0.80;
        r = r * (1 - k) + _c[0] * k * gv;
        g = g * (1 - k) + _c[1] * k * gv;
        b = b * (1 - k) + _c[2] * k * gv;
      }
      r *= wGrass; g *= wGrass; b *= wGrass;

      if (wDirt > 0.002) {
        // three earths — pale dust, red clay, dark damp loam
        mixC(C.dirt, C.dirtRed, clamp01((nL - 0.42) * 1.6), _c);
        mixC(_c, C.dirtDark, clamp01(nC * 0.50 + conc * 0.35 + grain * 0.25 - 0.14), _c);
        // ground that is walked on is *pale*: dust and packed earth, the tan
        // desire-lines that lace a DW field, not the dark loam of a bare patch
        const packedness = clamp01(track * 1.15 + apron * 0.55 + (1 - padD) * 0.9
          + smoothstep(clamp01((nP - 0.55) / 0.28)) * 0.55) * (1 - conc * 0.6);
        mixC(_c, C.dust, packedness * 0.62, _c);
        if (padD < 1.0) mixC(_c, C.packed, (1 - padD) * 0.62, _c);
        // gravel speckle: the fine grain reads as grit at 0.5 m/texel
        mixC(_c, C.gravel, smoothstep(clamp01((fine - 0.66) / 0.22)) * 0.45, _c);
        const k = wDirt * (1 - wRoad);
        r += _c[0] * k; g += _c[1] * k; b += _c[2] * k;
      }
      if (wRock > 0.002) {
        mixC(C.rock, C.rockDark, clamp01(nM * 0.55 + grain * 0.22 + 0.10), _c);
        mixC(_c, C.rockPale, smoothstep(clamp01((fine - 0.58) / 0.30)) * 0.55, _c);
        const k = wRock * (1 - wRoad);
        r += _c[0] * k; g += _c[1] * k; b += _c[2] * k;
      }
      if (wSand > 0.002) {
        mixC(C.sand, C.sandWet, clamp01(smoothstep(clamp01((10 - rivD) / 8)) * 0.8 + grain * 0.25), _c);
        mixC(_c, C.gravel, smoothstep(clamp01((fine - 0.62) / 0.26)) * 0.40, _c);
        // braided bars: the bank is not one white sheet. Damp silt in the
        // hollows, gravel stripes and dark mud where the water last stood.
        mixC(_c, C.silt, smoothstep(clamp01((0.52 - nC) / 0.24)) * 0.65, _c);
        mixC(_c, C.mud, clamp01(conc * 0.9) * smoothstep(clamp01((0.5 - nP) / 0.26)) * 0.6, _c);
        mixC(_c, C.dirt, smoothstep(clamp01((nP - 0.56) / 0.26)) * 0.42, _c);
        const k = wSand * (1 - wRoad);
        r += _c[0] * k; g += _c[1] * k; b += _c[2] * k;
      }
      if (wRoad > 0.002) {
        // gravel carriageway with two worn wheel ruts, grit and puddled hollows
        roadWeightAt(x, z, _rw);
        const rutDepth = 0.55 + 0.45 * smoothstep(clamp01((nP - 0.35) / 0.35));
        const rut = Math.exp(-Math.pow((Math.abs(_rw.d) - 1.30) / 0.50, 2)) * rutDepth
          + 0.45 * Math.exp(-Math.pow((Math.abs(_rw.d) - 2.35) / 0.42, 2)) * rutDepth;
        mixC(C.road, C.rut, clamp01(rut * 1.05 + grain * 0.24 + nM * 0.12), _c);
        mixC(_c, C.gravel, smoothstep(clamp01((fine - 0.55) / 0.30)) * 0.50 * (1 - rut * 0.6), _c);
        mixC(_c, C.dust, smoothstep(clamp01((nP - 0.58) / 0.26)) * 0.35, _c);
        const edge = smoothstep(clamp01((_rw.d - _rw.hw * 0.7) / 2.4));
        mixC(_c, C.dirt, edge * 0.45, _c);
        r = r * (1 - wRoad) + _c[0] * wRoad;
        g = g * (1 - wRoad) + _c[1] * wRoad;
        b = b * (1 - wRoad) + _c[2] * wRoad;
      }

      // standing water: hollows in bare/trodden ground and along the ruts.
      // Dark, slightly cool, and flagged hard in alpha so the specular lobe
      // gives it a sheen the surrounding mud does not have.
      const puddle = clamp01(conc * 1.25 - 0.10) *
        smoothstep(clamp01((nC - 0.44) / 0.20)) *
        clamp01(wDirt * 1.2 + wRoad * 0.9 + wSand * 0.5) *
        (1 - smoothstep(clamp01((sl - 0.05) / 0.09)));
      if (puddle > 0.004) {
        mixC(C.mud, C.wet, clamp01(puddle * 1.3), _c);
        const k = clamp01(puddle * 0.92);
        r = r * (1 - k) + _c[0] * k; g = g * (1 - k) + _c[1] * k; b = b * (1 - k) + _c[2] * k;
      }

      // Laid ground over everything above. Stone: 2 m andesite slabs in a
      // running bond, each slab its own tone, the joints a soft dark seam —
      // at 0.51 m a texel a hard one-texel line would alias into a grid. The
      // causeways are the same stone, paler and larger. Sand: the raked
      // alun-alun, pale and fine, with the rake as faint parallel bands.
      let paved = 0;
      if (GROUND.length) {
        groundAt(x, z, _gw);
        // A causeway is paved wherever it runs, the sand square included: the
        // first pass laid the sand over everything and the axis and cross
        // roads vanished at the alun-alun's edge, a mile of road going nowhere.
        const onRoad = clamp01(wRoad * 1.6);
        const sandW = _gw.sand * (1 - onRoad);
        _gw.pave = Math.max(_gw.pave, _gw.sand * onRoad);
        _gw.sand = sandW;
        if (_gw.pave > 0.002) {
          const big = wRoad > 0.3;
          const S = big ? 2.8 : 2.0;
          const row = Math.floor(z / S);
          const off = (row & 1) * S * 0.5;
          const col = Math.floor((x + off) / S);
          const fu = (x + off) / S - col, fv = z / S - row;
          const seam = Math.min(fu, 1 - fu, fv, 1 - fv) * S;          // metres to a joint
          const joint = 1 - smoothstep(clamp01(seam / 0.30));
          const hsh = Math.sin(col * 127.1 + row * 311.7) * 43758.5453;
          const tone = (hsh - Math.floor(hsh)) - 0.5;
          mixC(C.pave, C.paveDark, clamp01(0.30 + tone * 0.9 + nM * 0.25 + grain * 0.18), _c);
          if (big) mixC(_c, C.pavePale, 0.45, _c);
          mixC(_c, C.paveJoint, joint * 0.55, _c);
          const k = _gw.pave;
          r = r * (1 - k) + _c[0] * k; g = g * (1 - k) + _c[1] * k; b = b * (1 - k) + _c[2] * k;
          paved = k;
        }
        if (_gw.dirt > 0.002) {
          // A camp's floor: a thousand men have walked it to bare earth, in
          // tracks, round the grass that survived between the lines.
          mixC(C.dirt, C.dirtDark, clamp01(nC * 0.55 + grain * 0.3 + (0.5 - nP) * 0.5), _c);
          mixC(_c, C.packed, smoothstep(clamp01((nP - 0.45) / 0.3)) * 0.45, _c);
          const keep = smoothstep(clamp01((nP * 0.6 + nC * 0.4 - 0.62) / 0.16)) * 0.55;  // grass that survived
          // held off the tracks: the sand layer, composited here after the
          // roads, once erased the kraton's causeways the same way
          const k = _gw.dirt * (1 - keep) * (1 - paved) * (1 - clamp01(wRoad * 1.4));
          r = r * (1 - k) + _c[0] * k; g = g * (1 - k) + _c[1] * k; b = b * (1 - k) + _c[2] * k;
        }
        if (_gw.sand > 0.002) {
          // Not a raked white sheet — the first pass was one flat beige tone
          // that the haze took to white. Packed earth: the same patch and
          // clump fields the dirt uses, darker where it is trodden, and
          // struggling grass where nobody walks (see the grass mask).
          const rake = 0.5 + 0.5 * Math.sin(x * 6.3 + Math.sin(z * 0.08) * 2.0);
          mixC(C.courtSand, C.courtSandDark, clamp01(nM * 0.55 + grain * 0.30 + rake * 0.10 + (nP - 0.5) * 0.7), _c);
          mixC(_c, C.dirtDark, smoothstep(clamp01((nC - 0.55) / 0.3)) * 0.30, _c);
          mixC(_c, C.grassOlive, smoothstep(clamp01((0.42 - nP) / 0.2)) * 0.34, _c);
          const k = _gw.sand;
          r = r * (1 - k) + _c[0] * k; g = g * (1 - k) + _c[1] * k; b = b * (1 - k) + _c[2] * k;
          paved = Math.max(paved, k * 0.8);
        }
      }

      // bake ambient occlusion straight into the albedo (2-fetch shading path)
      let ao = clamp01(0.84 + (h - wide) * 0.10);
      ao *= 1 - 0.20 * wSand;
      ao *= 0.93 + 0.09 * nM;
      ao *= 1 - 0.10 * clamp01(wDirt * (1 - puddle));
      r *= ao; g *= ao; b *= ao;

      // alpha = "soft ground" weight: drives detail contrast + roughness
      const soft = clamp01((wGrass * 1.05 + wDirt * 0.55) * (1 - puddle * 0.95) * (1 - paved));

      const k4 = (j * MRES + i) * 4;
      macroData[k4] = clamp(r, 0, 255) | 0;
      macroData[k4 + 1] = clamp(g, 0, 255) | 0;
      macroData[k4 + 2] = clamp(b, 0, 255) | 0;
      macroData[k4 + 3] = clamp(soft * 255, 0, 255) | 0;
    }
  }

  const macroTex = colorTexture(macroData, MRES, MRES, { srgb: true, aniso: 1, mips: true });

  /* -------- field texture: height + grass mask, sampled by props.js -------- */
  const FRES = 512, FW = FRES + 1;
  const _gwF = { pave: 0, sand: 0, dirt: 0 };
  const fieldData = new Uint8Array(FRES * FRES * 4);
  const RANGE = Math.max(1, maxH - minH);
  for (let j = 0; j < FRES; j++) {
    const v = (j + 0.5) / FRES, z = v * SIZE - HALF;
    for (let i = 0; i < FRES; i++) {
      const u = (i + 0.5) / FRES, x = u * SIZE - HALF;
      const h = sampleH(u, v);
      const sl = sampleF(slopeG, u, v);
      const road = sampleU8(roadG, u, v);
      const rivD = sampleU8(rivG, u, v) * 255;
      const nD = sampleG(vDry, VN, u, v);
      const trail = sampleU8(trailG, u, v);
      const roadD = sampleU8(roadDG, u, v) * 255;
      const wox = (sampleG(vWarpA, VN, u, v) - 0.5) * 44;
      const woz = (sampleG(vWarpB, VN, u, v) - 0.5) * 44;
      const nP = sampleW(vPatch, VF, x + wox, z + woz);
      const nS = sampleW(vScree, VF, x + wox * 0.45, z + woz * 0.45);
      const nC = sampleW(vClump, VF, x + wox * 0.18, z + woz * 0.18);
      const nM = sampleG(vMed, VN, u, v);
      let grass = 1;
      grass *= 1 - clamp01(road * 1.6);
      grass *= 1 - smoothstep(clamp01((sl - 0.34) / 0.3));
      grass *= smoothstep(clamp01((rivD - 12) / 14));
      grass *= clamp01(1.25 - clamp01((h - 30) / 40));
      // the same bare-earth / track / scree fields the albedo bake uses, so a
      // tuft never grows out of a dirt patch it does not match the colour of
      grass *= 1 - clamp01(smoothstep(clamp01((nP * 0.55 + nC * 0.25 + nM * 0.20 - 0.625) / 0.135)) * 0.92);
      grass *= 1 - clamp01(trail * 0.90);
      grass *= 1 - clamp01((1 - smoothstep(clamp01((roadD - 2.5) / 7))) * 0.75);
      grass *= 1 - smoothstep(clamp01((nS - 0.66) / 0.12)) * smoothstep(clamp01((nP - 0.45) / 0.24)) * 0.8;
      grass *= 0.62 + 0.62 * (1 - nD);
      for (const s of SITES) {
        const r = s.pad > 0 ? s.pad : s.radius;
        const d = Math.hypot(x - s.x, z - s.z);
        grass *= smoothstep(clamp01((d - r * 0.8) / (r * 0.55)));
      }
      // nothing grows through laid stone, and little in a raked square
      if (GROUND.length) { groundAt(x, z, _gwF); grass *= (1 - _gwF.pave) * (1 - _gwF.sand * 0.62) * (1 - _gwF.dirt * 0.8); }
      const k4 = (j * FRES + i) * 4;
      fieldData[k4] = clamp(((h - minH) / RANGE) * 255, 0, 255) | 0;
      fieldData[k4 + 1] = clamp(clamp01(grass) * 255, 0, 255) | 0;
      fieldData[k4 + 2] = clamp(clamp01(sl) * 255, 0, 255) | 0;
      fieldData[k4 + 3] = 255;
    }
  }
  const fieldTex = dataTexture(fieldData, FRES, FRES, { linear: true, mips: false });

  /* ============================== material ============================ */

  /**
   * Custom terrain shader. A full MeshStandardMaterial costs ~2x more per
   * fragment than this on the software rasteriser and the ground is matte
   * anyway, so we light it by hand: three's own light + shadow uniforms, a
   * wrapped lambert term, hemisphere bounce and one cheap Blinn lobe.
   */
  const uni = THREE.UniformsUtils.merge([
    THREE.UniformsLib.lights,
    THREE.UniformsLib.fog,
    {
      uMacro: { value: null }, uDetA: { value: null }, uDetB: { value: null },
      uTerrA: { value: new THREE.Vector4() }, uTerrB: { value: new THREE.Vector4() },
      uTerrC: { value: new THREE.Vector4() }, uTerrD: { value: new THREE.Vector4() },
      uShadowC: { value: new THREE.Vector4() },
      uFarRock: { value: new THREE.Color(0x625d55).convertSRGBToLinear() },
    },
  ]);
  uni.uMacro.value = macroTex;
  uni.uDetA.value = detA;
  uni.uDetB.value = detB;
  // x = 1/worldSize, y = fine detail freq, z = (unused), w = bump
  uni.uTerrA.value.set(1 / SIZE, 0.55, 0.070, 0.72);
  // x = fine fade start, y = fine fade end, z = spec power, w = spec gain
  //
  // Golden hour puts the key at 27 degrees, so flat ground takes N.L ~0.45 and
  // is meant to be dark; the highlights of that hour are supposed to be the
  // grazing sheen a low sun lays across grass and road. It was not arriving.
  // A validated model of this shader -- albedo ~0.2 times lit ~7 over pi, or
  // linear 0.45, code 189 -- matches the measured p99 of every ordinary frame
  // and also predicts the key-doubling case, so the diffuse term is behaving;
  // what was missing is this lobe. At power 64 it was narrower than a polished
  // metal highlight and at gain 0.075 it could not have registered anyway.
  uni.uTerrB.value.set(95.0, 300.0, 36.0, 0.40);
  // The second (gravel) octave: x/y are its fade range, z/w its near/far UV
  // frequency. It is deliberately still alive at 400 m, because the first pass
  // faded *all* detail out at 48 m and the ground beyond that read as flat
  // smooth brown with a visible blur boundary across the road.
  uni.uTerrD.value.set(150.0, 620.0, 0.400, 0.052);
  // x = wrap softness, y = bounce gain, z = unused, w = unused
  uni.uTerrC.value.set(0.30, 1.0, 1e6, 1e7);
  uni.uShadowC.value.set(0, 0, 0, 0);

  const material = new THREE.ShaderMaterial({
    uniforms: uni, lights: true, fog: true, dithering: true,
    vertexShader: `
      #include <common>
      #include <fog_pars_vertex>
      #include <shadowmap_pars_vertex>
      varying vec3 vWPos;
      varying vec3 vNrm;
      varying vec3 vVPos;
      void main() {
        vec3 objectNormal = normal;
        vec3 transformedNormal = normalMatrix * objectNormal;
        vNrm = transformedNormal;
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vec4 mvPosition = viewMatrix * worldPosition;
        vWPos = worldPosition.xyz;
        vVPos = mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
        #include <shadowmap_vertex>
        #include <fog_vertex>
      }`,
    fragmentShader: `
      #include <common>
      #include <packing>
      #include <dithering_pars_fragment>
      #include <fog_pars_fragment>
      #include <lights_pars_begin>
      #include <shadowmap_pars_fragment>
      #include <shadowmask_pars_fragment>
      uniform sampler2D uMacro;
      uniform sampler2D uDetA;
      uniform sampler2D uDetB;
      uniform vec4 uTerrA;
      uniform vec4 uTerrB;
      uniform vec4 uTerrC;
      uniform vec4 uTerrD;
      uniform vec4 uShadowC;
      uniform vec3 uFarRock;
      varying vec3 vWPos;
      varying vec3 vNrm;
      varying vec3 vVPos;

      void main() {
        // The macro splat only covers the authored map, but the rim hills and
        // the horizon mountains run kilometres past its edge. A clamped
        // sampler out there can only smear one row of texels radially
        // outwards, which reads as vertical corduroy across the whole range.
        // Mirror the map instead: the triangle wave is the identity on [0,1]
        // and reflects beyond it, so the distance keeps real, seamless terrain
        // variation. Pull it towards bare rock as it goes so the far peaks do
        // not wear a recognisable copy of the valley's fields.
        vec2 muv = vWPos.xz * uTerrA.x + 0.5;
        float depth0 = -vVPos.z;
        float nearBias = 1.0 - smoothstep(30.0, 110.0, depth0);
        // 0.5 m per macro texel, seen at a grazing angle: same mip-selector
        // problem as the detail sets below, milder because the content is low
        // frequency by design.
        vec4 mac = texture2D(uMacro, 1.0 - abs(fract(muv * 0.5) * 2.0 - 1.0), -0.45 * nearBias);
        float outs = smoothstep(0.0, 0.30,
          max(max(-muv.x, muv.x - 1.0), max(-muv.y, muv.y - 1.0)));
        mac.rgb = mix(mac.rgb, mix(mac.rgb, uFarRock, 0.66), outs);
        // Two detail octaves, not one. A single 1.8 m fibre texture gives the
        // uniform orange-peel "popcorn" grain the critic measured at 1:1; the
        // gravel set at a coarser scale adds stones, ruts and clumping, and it
        // survives to 600 m so the near field never terminates in a blur line.
        float depth = -vVPos.z;
        float fade = 1.0 - smoothstep(uTerrB.x, uTerrB.y, depth);
        float fadeM = 1.0 - smoothstep(uTerrD.x, uTerrD.y, depth);
        float soft = mac.a;
        vec2 duv = vWPos.xz * uTerrA.y + (mac.rg - 0.5) * 1.7;
        vec2 guv = vWPos.xz * mix(uTerrD.w, uTerrD.z, fadeM) + (mac.gb - 0.5) * 0.9;
        // A negative mip bias on the near fetches. Ground is only ever seen at
        // a grazing angle, and with anisotropy 1 (the software-rasteriser
        // default) the mip selector takes the LONG axis of the footprint, so
        // the 1.8 m fibre set is resolved to its own average from about 4 m
        // out. That is the "smooth brown felt" read: the octave is there, the
        // sampler is throwing it away. Faded out with distance so nothing past
        // ~100 m starts to shimmer.
        vec4 det = texture2D(uDetA, duv, -0.70 * fade);
        vec4 gvl = texture2D(uDetB, guv, -0.45 * fadeM);

        // centred contrast so neither octave shifts the average brightness.
        // The fine octave used to be scaled to 0.45 on hard ground and 1.20 on
        // soft — so a road, the surface with the least macro variation to
        // start with, also got the least grain, and read as felt.
        float vF = (det.b - 0.59) * (0.98 * fade) * (0.80 + 0.42 * soft);
        float vG = (gvl.b - 0.58) * (0.26 + 0.30 * fadeM) * (1.05 - 0.45 * soft);
        float grain = clamp(1.0 + vF + vG, 0.42, 1.72);
        // the AO channels have to fade with distance the way grain does, or the
        // minified detail textures alias into moire on anything past ~300 m.
        float dao = mix(0.5, det.a, fade) * 0.62 + mix(0.5, gvl.a, fadeM) * 0.38;
        vec3 albedo = mac.rgb * grain * (0.76 + 0.32 * dao);
        // stone is cooler and less saturated than the earth it sits in
        albedo = mix(albedo, albedo * vec3(0.93, 0.97, 1.09), clamp(vG * 2.6, 0.0, 0.55) * (1.0 - soft * 0.55));
        // The finest octave moved LUMINANCE only, so at 5-8 m the ground was
        // one flat hue with a brightness wobble on it — felt, not soil.
        // det.rg is the gradient of the same height field the luminance came
        // from: the same spatial frequency, a quarter phase away, which is
        // exactly the decorrelated signal a per-grain hue break needs. Warm
        // dry blade against cool green on turf; pale grit against damp earth
        // on a road.
        float hueF = ((det.r - 0.5) + (det.g - 0.5)) * fade;
        albedo *= vec3(1.0 + hueF * (0.15 + 0.06 * soft),
                       1.0 + hueF * (0.02 + 0.05 * soft),
                       1.0 - hueF * (0.15 - 0.03 * soft));

        vec3 gN = normalize(vNrm);
        vec2 dn = ((det.rg - 0.5) * (fade * (0.5 + 0.9 * soft))
                 + (gvl.rg - 0.5) * (fadeM * (0.50 - 0.28 * soft))) * uTerrA.w;
        vec3 n = normalize(gN + (viewMatrix * vec4(dn.x, 0.0, dn.y, 0.0)).xyz);

        float shadow = mix(getShadowMask(), 1.0, smoothstep(uTerrC.z, uTerrC.w, distance(vWPos.xz, uShadowC.xy)));
        #if NUM_DIR_LIGHTS > 0
          // Shadow-map acne shows up as thin dark bands streaked across sunlit
          // hillsides wherever the surface grazes the light. Those texels are
          // nearly unlit anyway, so fading the mask out as N.L approaches zero
          // costs no real occlusion and removes the banding.
          shadow = mix(1.0, shadow, smoothstep(0.0, 0.20, dot(gN, directionalLights[0].direction)));
        #endif
        vec3 V = normalize(-vVPos);
        vec3 lit = ambientLightColor + vec3(0.26, 0.32, 0.42);
        vec3 spec = vec3(0.0);

        #if NUM_HEMI_LIGHTS > 0
          float hd = 0.5 * dot(n, hemisphereLights[0].direction) + 0.5;
          lit += mix(hemisphereLights[0].groundColor, hemisphereLights[0].skyColor, hd) * uTerrC.y;
        #endif

        #if NUM_DIR_LIGHTS > 0
          {
            vec3 L = directionalLights[0].direction;
            float ndl = dot(n, L);
            float wrap = clamp((ndl + uTerrC.x) / (1.0 + uTerrC.x), 0.0, 1.0);
            lit += directionalLights[0].color * (wrap * shadow);
            vec3 H = normalize(L + V);
            float sp = pow(max(dot(n, H), 0.0), uTerrB.z) * uTerrB.w * (1.0 - mac.a * 0.72);
            spec += directionalLights[0].color * (sp * shadow * step(0.0, ndl));
          }
        #endif
        // Every directional light after the key, shadowless. This has to be a
        // loop: the cascaded-shadow rig puts its colourless extra cascades at
        // indices 1..N-1 and the real sky fill *after* them, so a hard-coded
        // directionalLights[1] reads a black dummy and the ground never gets
        // the cool fill the characters do. The dummies cost nothing here —
        // their colour is zero.
        #if NUM_DIR_LIGHTS > 1
          for (int i = 1; i < NUM_DIR_LIGHTS; i++) {
            vec3 L = directionalLights[i].direction;
            float wrap = clamp((dot(n, L) + uTerrC.x) / (1.0 + uTerrC.x), 0.0, 1.0);
            lit += directionalLights[i].color * wrap;
          }
        #endif

        gl_FragColor = vec4(albedo * lit * RECIPROCAL_PI + spec, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
        #include <dithering_fragment>
      }`,
  });

  /* ============================== geometry ============================ */

  // The authored far field contains more relief than a 20 m L3 mesh can
  // represent without aliasing into long, steep triangle slivers. Build one
  // deterministic render-only height lattice aligned to every L3/L4 knot and
  // project its eight-neighbour slopes to a 14 m rise limit. The playable
  // 320 m square is pinned exactly to `shapeFull`, so heightAt/walkHeightAt,
  // roads, pads and the L2 boundary remain bit-identical. This is startup-only
  // work (no frame allocations or updates) and the same lattice feeds both far
  // rings, keeping their shared boundary exact.
  const FAR_EXT = 1480, FAR_STEP = 20, FAR_N = FAR_EXT * 2 / FAR_STEP + 1;
  let farRender = null;
  function buildFarRender() {
    const y = new Float32Array(FAR_N * FAR_N);
    const fixed = new Uint8Array(y.length);
    for (let j = 0; j < FAR_N; j++) {
      const z = -FAR_EXT + j * FAR_STEP;
      for (let i = 0; i < FAR_N; i++) {
        const x = -FAR_EXT + i * FAR_STEP, k = j * FAR_N + i;
        y[k] = shapeFull(x, z);
        fixed[k] = Math.max(Math.abs(x), Math.abs(z)) <= 320 ? 1 : 0;
      }
    }
    const dirs = [[1, 0], [0, 1], [1, 1], [-1, 1]];
    const MAX_RISE = 14;
    for (let pass = 0; pass < 96; pass++) {
      let worst = 0;
      for (let j = 0; j < FAR_N; j++) for (let i = 0; i < FAR_N; i++) {
        const a = j * FAR_N + i;
        for (const [di, dj] of dirs) {
          const ii = i + di, jj = j + dj;
          if (ii < 0 || ii >= FAR_N || jj >= FAR_N) continue;
          const b = jj * FAR_N + ii;
          if (fixed[a] && fixed[b]) continue;
          const d = y[b] - y[a], excess = Math.abs(d) - MAX_RISE;
          if (excess <= 0) continue;
          if (excess > worst) worst = excess;
          const s = d < 0 ? -1 : 1;
          if (fixed[a]) y[b] -= s * excess;
          else if (fixed[b]) y[a] += s * excess;
          else { y[a] += s * excess * 0.5; y[b] -= s * excess * 0.5; }
        }
      }
      if (worst < 0.001) break;
    }
    farRender = y;
  }
  function renderHeightAt(x, z) {
    if (Math.max(Math.abs(x), Math.abs(z)) <= 320) return shapeFull(x, z);
    if (!farRender) buildFarRender();
    const gx = clamp((x + FAR_EXT) / FAR_STEP, 0, FAR_N - 1);
    const gz = clamp((z + FAR_EXT) / FAR_STEP, 0, FAR_N - 1);
    const i = Math.min(FAR_N - 2, Math.floor(gx));
    const j = Math.min(FAR_N - 2, Math.floor(gz));
    const tx = gx - i, tz = gz - j;
    const a = farRender[j * FAR_N + i], b = farRender[j * FAR_N + i + 1];
    const c = farRender[(j + 1) * FAR_N + i], d = farRender[(j + 1) * FAR_N + i + 1];
    return lerp(lerp(a, b, tx), lerp(c, d, tx), tz);
  }

  /**
   * One concentric LOD ring (or the solid core when inner === 0).
   * `nbOuter` / `nbInner` are the cell sizes of the rings on either side of
   * this one; the skirt heights are measured against their chords.
   */
  function buildRing(inner, outer, cell, nbOuter = cell, nbInner = cell) {
    // n MUST be even: the hole test and the inner skirt walk both index the
    // vertex map through n/2, and an odd n makes those indices fractional,
    // which silently writes NaN positions into the skirt (and a NaN bounding
    // sphere, so the ring is never culled correctly either).
    let n = Math.max(2, Math.round((outer * 2) / cell));
    if (n & 1) n++;
    const hn = n / 2;
    const hi = Math.round(inner / cell);              // hole half-size in cells
    const vertsX = n + 1;
    const pos = [], nor = [], idx = [];
    const map = new Int32Array(vertsX * vertsX).fill(-1);
    const eps = Math.max(1.1, cell * 0.62);

    const LIM = HALF - 1.5;
    const vy = (x, z) => (x > -LIM && x < LIM && z > -LIM && z < LIM) ? heightAt(x, z) : renderHeightAt(x, z);
    const addVert = (ci, cj) => {
      const k = cj * vertsX + ci;
      if (map[k] >= 0) return map[k];
      const x = (ci - hn) * cell, z = (cj - hn) * cell;
      const y = vy(x, z);
      const hl = vy(x - eps, z), hr = vy(x + eps, z), hd = vy(x, z - eps), hu = vy(x, z + eps);
      let nx = hl - hr, ny = 2 * eps, nz = hd - hu;
      const inv = 1 / Math.hypot(nx, ny, nz);
      const id = pos.length / 3;
      pos.push(x, y, z); nor.push(nx * inv, ny * inv, nz * inv);
      map[k] = id;
      return id;
    };

    for (let cj = 0; cj < n; cj++) {
      for (let ci = 0; ci < n; ci++) {
        if (hi > 0) {
          const dx = Math.abs(ci - hn + 0.5), dz = Math.abs(cj - hn + 0.5);
          if (dx < hi && dz < hi) continue;
        }
        const a = addVert(ci, cj), b = addVert(ci + 1, cj), c = addVert(ci + 1, cj + 1), d = addVert(ci, cj + 1);
        idx.push(a, d, c, a, c, b);
      }
    }

    // Boundary connectors reproduce the neighbouring ring's chord at both
    // endpoints. Unlike the old guessed downward wall, they bridge upward or
    // downward as required, so every LOD chord crossing is covered without a
    // tall exposed skirt. Each quad is emitted once and its winding is derived
    // from its actual rise/fall below; a fixed winding culls exactly half of
    // bidirectional connectors.
    const surfaceIdx = idx.length;
    let dropMax = 0;
    const MIN_DROP = Math.min(1.2, cell * 0.6);
    /**
     * Height of a chord sampled every `q` metres along one edge of the
     * boundary square of half-size R, evaluated at the point (px, pz).
     * The two neighbouring rings share this square and both of their knot
     * sets are multiples of their own cell from -R, so this reproduces the
     * neighbour's boundary polyline exactly.
     */
    const chordOn = (R, q, px, pz, varyX) => {
      const t = varyX ? px : pz;
      const k = Math.floor((t + R) / q + 1e-6);
      const t0 = -R + k * q, t1 = t0 + q;
      const y0 = varyX ? vy(t0, pz) : vy(px, t0);
      const y1 = varyX ? vy(t1, pz) : vy(px, t1);
      return y0 + (y1 - y0) * ((t - t0) / q);
    };
    const addSkirt = (ring, outward, R, nb) => {
      for (let s = 0; s < ring.length; s++) {
        const [i0, j0] = ring[s], [i1, j1] = ring[(s + 1) % ring.length];
        const a = map[j0 * vertsX + i0], b = map[j1 * vertsX + i1];
        if (a < 0 || b < 0) continue;
        const ax = pos[a * 3], ay = pos[a * 3 + 1], az = pos[a * 3 + 2];
        const bx = pos[b * 3], by = pos[b * 3 + 1], bz = pos[b * 3 + 2];
        // The crack this edge has to plug is the vertical distance between
        // MY chord and the NEIGHBOUR ring's chord over the same span. Nine
        // samples is exact for these ratios: the extremum of |linear-linear|
        // always sits on a knot of the finer of the two.
        let need = 0, reachA = ay - MIN_DROP, reachB = by - MIN_DROP;
        if (nb !== cell) {
          const varyX = Math.abs(bx - ax) > Math.abs(bz - az);
          const otherA = chordOn(R, nb, ax, az, varyX);
          const otherB = chordOn(R, nb, bx, bz, varyX);
          // Join MY surface chord to the neighbour's chord, whether that
          // neighbour is above or below it. A downward-only wall cannot cover
          // half of the possible chord crossings and was the actual source
          // of the L2/L3 sky wedges near the world boundary.
          reachA = otherA + Math.sign(otherA - ay) * 0.2;
          reachB = otherB + Math.sign(otherB - by) * 0.2;
          for (let k = 0; k <= 8; k++) {
            const f = k * 0.125;
            const px = ax + (bx - ax) * f, pz = az + (bz - az) * f;
            const d = Math.abs((ay + (by - ay) * f) - chordOn(R, nb, px, pz, varyX));
            if (d > need) need = d;
          }
        }
        const drop = Math.max(Math.abs(reachA - ay), Math.abs(reachB - by), need);
        const c = pos.length / 3;
        if (drop > dropMax) dropMax = drop;
        pos.push(ax, reachA, az, bx, reachB, bz);
        const sgn = outward ? -1 : 1;
        const la = Math.hypot(ax, az) || 1, lb = Math.hypot(bx, bz) || 1;
        const nax = (ax / la) * sgn, naz = (az / la) * sgn;
        const nbx = (bx / lb) * sgn, nbz = (bz / lb) * sgn;
        nor.push(nax, 0.12, naz, nbx, 0.12, nbz);
        // The connector may rise OR fall. A fixed index order reverses its
        // geometric normal whenever it rises, which was why the exact bridge
        // above was still culled on precisely the formerly-open half of each
        // seam. Orient both triangles from their actual positions so outer
        // boundaries face the centre and inner boundaries face away.
        const tx = (nax + nbx) * 0.5, tz = (naz + nbz) * 0.5;
        const face = (i0, i1, i2) => {
          const x0 = pos[i0 * 3], y0 = pos[i0 * 3 + 1], z0 = pos[i0 * 3 + 2];
          const ux = pos[i1 * 3] - x0, uy = pos[i1 * 3 + 1] - y0, uz = pos[i1 * 3 + 2] - z0;
          const vx = pos[i2 * 3] - x0, vy = pos[i2 * 3 + 1] - y0, vz = pos[i2 * 3 + 2] - z0;
          const nx = uy * vz - uz * vy, nz = ux * vy - uy * vx;
          if (nx * tx + nz * tz >= 0) idx.push(i0, i1, i2);
          else idx.push(i0, i2, i1);
        };
        face(a, c, b); face(b, c, c + 1);
      }
    };
    const outerRing = [];
    for (let i = 0; i < n; i++) outerRing.push([i, 0]);
    for (let j = 0; j < n; j++) outerRing.push([n, j]);
    for (let i = n; i > 0; i--) outerRing.push([i, n]);
    for (let j = n; j > 0; j--) outerRing.push([0, j]);
    addSkirt(outerRing, true, hn * cell, nbOuter);
    if (hi > 0) {
      const a0 = hn - hi, a1 = hn + hi;
      const innerRing = [];
      for (let i = a1; i > a0; i--) innerRing.push([i, a0]);
      for (let j = a0; j < a1; j++) innerRing.push([a0, j]);
      for (let i = a0; i < a1; i++) innerRing.push([i, a1]);
      for (let j = a1; j > a0; j--) innerRing.push([a1, j]);
      addSkirt(innerRing, false, hi * cell, nbInner);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    // Surface indices come first, skirt indices last, so a capture can shoot
    // the ring with and without its skirt (`terrain.setSkirts(false)`) and see
    // which of the two owns a pixel. Diagnostic only; no effect in game.
    geo.userData.surfaceIndices = surfaceIdx;
    geo.userData.skirtIndices = idx.length - surfaceIdx;
    geo.userData.dropMax = dropMax;
    return geo;
  }

  const group = new THREE.Group(); group.name = 'terrain';
  const meshes = [];

  /* ------------------------------ water ------------------------------ */
  let waterMat = null;
  function buildWater() {
    const pos = [], uv = [], idx = [];
    /* An arena with no river builds no water — an empty mesh.
     *
     * This was SAFE BY ACCIDENT before, and the first attempt to make it
     * explicit BROKE it. The original read S1 = RIVER.len = undefined, so n
     * was NaN and `i <= NaN` is false: the loop never ran. "Tidying" that to
     * S1 = 0 made n = 0, and `i <= 0` runs ONCE — calling RIVER.at(0) on an
     * empty polyline, which reads undefined[0] and threw during terrain init.
     * The safety had come from NaN, not from undefined, and removing the
     * accident removed the safety. n = -1 is the explicit form: no iterations. */
    const S0 = 0, S1 = HAS_RIVER ? RIVER.len : 0;
    const step = 7;
    const n = HAS_RIVER ? Math.floor((S1 - S0) / step) : -1;
    let row = 0;
    for (let i = 0; i <= n; i++) {
      const s = S0 + i * step;
      RIVER.at(s, _p);
      if (Math.abs(_p.x) > HALF + 40 || Math.abs(_p.z) > HALF + 40) { row++; continue; }
      const y = waterLineAt(s);
      const wdt = waterHalfWidth(s);
      const nx = -_p.tz, nz = _p.tx;
      pos.push(_p.x - nx * wdt, y, _p.z - nz * wdt);
      pos.push(_p.x + nx * wdt, y, _p.z + nz * wdt);
      uv.push(0, s * 0.05, 1, s * 0.05);
    }
    const cnt = pos.length / 3 / 2;
    for (let i = 0; i < cnt - 1; i++) {
      const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
      idx.push(a, c, b, b, c, d);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    waterMat = new THREE.MeshStandardMaterial({
      color: 0x445e60, roughness: 0.13, metalness: 0.04, transparent: true, opacity: 0.80,
      fog: true, side: THREE.DoubleSide,
    });
    const wuni = { uWTime: { value: 0 }, uWDet: { value: detA } };
    waterMat.userData.uni = wuni;
    waterMat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, wuni);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWP;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n\tvWP = (modelMatrix*vec4(position,1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWP;\nuniform float uWTime;\nuniform sampler2D uWDet;')
        .replace('#include <normal_fragment_maps>', `
          vec2 w1 = texture2D(uWDet, vWP.xz*0.09 + vec2(uWTime*0.012, uWTime*0.03)).rg - 0.5;
          vec2 w2 = texture2D(uWDet, vWP.xz*0.24 - vec2(uWTime*0.021, uWTime*0.008)).rg - 0.5;
          vec3 wn = vec3((w1.x*0.7+w2.x*0.4), 0.0, (w1.y*0.7+w2.y*0.4)) * 0.55;
          normal = normalize(normal + (viewMatrix*vec4(wn,0.0)).xyz);`);
    };
    waterMat.customProgramCacheKey = () => 'sb-water-1';
    const m = new THREE.Mesh(geo, waterMat);
    m.name = 'river'; m.receiveShadow = false; m.castShadow = false;
    m.renderOrder = 1;
    return m;
  }

  const api = {
    heightAt, normalAt, slopeAt, materialAt,
    walkHeightAt, addWalkway, onWalkway, walkways,
    size: SIZE, half: HALF, group, mesh: null,
    noise, res: RES, heights, step: STEP,
    /** Full authored surface, valid outside the sampled grid too (debug/probes). */
    shape: shapeFull,
    minH, maxH,
    /* Which battlefield THIS terrain was carved as. Read from the terrain that
     * was actually built, not from the URL that asked for it: resolveArena()
     * falls back to the default on an unknown id, so the URL and the world can
     * disagree, and only the world is the truth. */
    arena: ARENA_ID,
    arenaName: ARENA_NAME,
    hasRiver: HAS_RIVER,
    /** shared with props.js / bases.js */
    field: { tex: fieldTex, min: minH, max: maxH, size: SIZE, half: HALF, res: FRES },
    macroTex, detailA: detA, detailB: detB,
    sites: SITES,
    roadWeight(x, z) { roadWeightAt(x, z, _rw); return _rw.w; },
    riverDist(x, z) { RIVER.query(x, z, _q); return _q.d; },
    /**
     * How deep the water is at (x, z), and which way is out.
     *
     * The Brantas has stranded three separate probes and, on the evidence of
     * two captures, would strand a player: the channel is 4.6 m below its own
     * water line and nothing in the game slowed you down on the way in or
     * pushed you back out. `out.push` is the unit vector AWAY from the
     * centreline, found from the gradient of the distance field rather than
     * from the spline's normal, because the field is what the depth is
     * actually computed from and the two disagree on the bends.
     */
    waterAt(x, z, out) {
      out = out || {};
      RIVER.query(x, z, _q);
      out.depth = 0; out.px = 0; out.pz = 0;
      /* Only inside the strip that is actually DRAWN. The first version used a
       * 22.5 m band around the centreline, where the water is 3.4-6.0 m wide,
       * so it called dry bank "river" for sixteen metres either side. */
      const half = waterHalfWidth(_q.s);
      if (_q.d > half + 1.2) return out;
      const ground = walkHeightAt(x, z);
      const surface = waterLineAt(_q.s);
      out.depth = surface > ground ? surface - ground : 0;
      if (out.depth <= 0) return out;
      const e = 1.2;
      RIVER.query(x + e, z, _q); const dxp = _q.d;
      RIVER.query(x - e, z, _q); const dxm = _q.d;
      RIVER.query(x, z + e, _q); const dzp = _q.d;
      RIVER.query(x, z - e, _q); const dzm = _q.d;
      const gx = dxp - dxm, gz = dzp - dzm;
      const len = Math.hypot(gx, gz);
      if (len > 1e-5) { out.px = gx / len; out.pz = gz / len; }
      return out;
    },
    material,
    /**
     * Diagnostic: draw the LOD rings with or without their crack-hiding
     * skirts. Skirt indices are appended after the surface indices, so a
     * draw-range truncation removes exactly the skirts and nothing else.
     * Used by `--scenario world_rings_r5` to attribute horizon pixels.
     */
    setSkirts(on) {
      for (const m of meshes) {
        const g = m.geometry, s = g.userData.surfaceIndices | 0;
        if (on) g.setDrawRange(0, Infinity); else g.setDrawRange(0, s);
      }
    },
    /** Diagnostic: per-ring geometry census (tris, skirt tris, drop, radius). */
    ringInfo() {
      return meshes.map(m => {
        const g = m.geometry, u = g.userData;
        return {
          name: m.name,
          surfTris: (u.surfaceIndices | 0) / 3,
          skirtTris: (u.skirtIndices | 0) / 3,
          drop: +(u.dropMax || 0).toFixed(2),
          bsR: +(g.boundingSphere?.radius || 0).toFixed(0),
        };
      });
    },
  };

  return {
    name: 'terrain', order: ORDER.WORLD,
    init(w) {
      // Concentric LOD rings. Every boundary is aligned to both neighbouring
      // grids; each connector evaluates the other grid's piecewise chord, so
      // unequal knot spacing cannot expose sky between their polylines.
      //
      // The far pair used to be [304,912,19] + [912,2432,48]. Two things were
      // wrong with that. The camera far plane is 900 m (CFG.camera.far), so
      // everything past ~1160 m of world radius is clipped and the 2432 m
      // ring was 18 k triangles of geometry that could never be seen; and the
      // 912 m seam landed exactly on the skyline crest (measured: the maximum
      // elevation angle along an outward ray is reached at 770-960 m for
      // every azimuth), which is the one place a LOD seam is against sky.
      // Now the fine ring runs to 1240 m — past the far plane from every point
      // on the playable 260 m grid, rather than merely "almost every" camera.
      // The 40 m insurance ring only needs another 240 m: shrinking its
      // unreachable exterior more than pays for the L3 extension, so this
      // closes the far-plane seam with fewer triangles overall.
      const rings = [
        [0, 48, 1.0],
        [48, 160, 2.0],
        [160, 320, 8.0],
        [320, 1240, 20.0],
        [1240, 1480, 40.0],
      ];
      for (let i = 0; i < rings.length; i++) {
        const [a, b, c] = rings[i];
        const geo = buildRing(a, b, c, rings[i + 1] ? rings[i + 1][2] : c, rings[i - 1] ? rings[i - 1][2] : c);
        const m = new THREE.Mesh(geo, material);
        m.name = 'ground_L' + i;
        m.receiveShadow = i < 2;
        m.castShadow = false;
        m.matrixAutoUpdate = false;
        m.updateMatrix();
        group.add(m); meshes.push(m);
      }
      // The render-only lattice is startup scaffolding; all sampled heights
      // now live in immutable GPU buffers, so release its 88 KB CPU array.
      farRender = null;
      api.mesh = meshes[0];
      api.meshes = meshes;

      const water = buildWater();
      group.add(water);
      api.water = water;

      const maxA = w.renderer?.capabilities?.getMaxAnisotropy?.() || 1;
      const aniso = Math.min(Number(new URLSearchParams(location.search).get('taniso') ?? 1), maxA);
      macroTex.anisotropy = aniso; detA.anisotropy = aniso; detB.anisotropy = aniso;

      w.scene.add(group);
      w.terrain = api;
    },
    update(w, dt, alpha, realDt) {
      if (waterMat?.userData.uni) waterMat.userData.uni.uWTime.value += realDt;
      const t = w.lighting?.target, cam = w.lighting?.sun?.shadow?.camera;
      if (t && cam) {
        const r = Math.abs(cam.right) || 60;
        uni.uShadowC.value.set(t.position.x, t.position.z, 0, 0);
        uni.uTerrC.value.z = r * 0.68; uni.uTerrC.value.w = r * 0.94;
        setShadowBox(t.position.x, t.position.z, r);
      }
    },
    /**
     * Headless capture only: the software rasteriser lets the GL command queue
     * run tens of frames ahead, which makes Playwright screenshot a stale
     * compositor frame. One 1x1 readPixels per frame keeps the queue at depth
     * 1 so every capture shows what was actually simulated. No-op in game.
     */
    lateUpdate(w) {
      if (!CFG.debug.headless) return;
      const gl = _gl || (_gl = w.renderer.getContext());
      if (gl) gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, _px);
    },
    dispose() {
      group.traverse(o => { o.geometry?.dispose(); });
      material.dispose(); waterMat?.dispose();
      macroTex.dispose(); detA.dispose(); detB.dispose(); fieldTex.dispose();
    },
  };
}
